/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/main.tsx
 * DESCRIPTION: Application entry point with Clerk authentication,
 *              Organizations support, and Admin authentication enabled.
 *
 * IMPORTS IN:
 * - None (root entry point)
 *
 * DEPENDS ON:
 * - @clerk/react
 * - @clerk/themes
 * - src/contexts/AdminAuthContext
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 2.0.2
 * @created 2025-01-15
 * @updated 2026-03-27 - Added Clerk Organizations support, dark mode
 *                       initialization, and AdminAuthProvider
 * @updated 2026-04-03 - Removed invalid Organization URL props from
 *                       ClerkProvider; these belong on Organization
 *                       components or Clerk Dashboard for @clerk/react
 * ─────────────────────────────────────────────────────────────────────
 */

import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import './index.css'

// Clerk imports
import { ClerkProvider } from '@clerk/react'

// Admin Auth Context
import { AdminAuthProvider } from './contexts/AdminAuthContext'

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

if (!PUBLISHABLE_KEY) {
  throw new Error("Missing Clerk Publishable Key in environment variables")
}

// ─────────────────────────────────────────────────────────────────────
// Dark Mode Initialization
// Runs BEFORE React renders to prevent flash of wrong theme
// ─────────────────────────────────────────────────────────────────────
const initializeDarkMode = () => {
  const savedDarkMode = localStorage.getItem('darkMode')
  if (savedDarkMode !== null) {
    // Use saved preference
    if (JSON.parse(savedDarkMode)) {
      document.documentElement.classList.add('dark')
    }
  } else {
    // Fall back to system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    if (prefersDark) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('darkMode', 'true')
    }
  }
}

// Initialize dark mode immediately
initializeDarkMode()

// ─────────────────────────────────────────────────────────────────────
// Render App
// Note: Organization redirect URLs are configured in:
// 1. Clerk Dashboard > Organization Settings, OR
// 2. Individual components: <CreateOrganization afterCreateOrganizationUrl="/dashboard" />
// ─────────────────────────────────────────────────────────────────────
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      appearance={{
        variables: {
          colorPrimary: '#0ea5e9',
          colorBackground: '#ffffff',
          colorInputBackground: '#f9fafb',
          colorInputText: '#111827',
        },
        elements: {
          formButtonPrimary: 'bg-primary-600 hover:bg-primary-700 text-white',
          card: 'bg-white rounded-xl shadow-lg',
          headerTitle: 'text-xl font-bold text-gray-900',
          headerSubtitle: 'text-gray-600',
          socialButtonsBlockButton: 'border border-gray-300 hover:bg-gray-50',
          formFieldInput: 'border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500',
        }
      }}
    >
      <AdminAuthProvider>
        <App />
      </AdminAuthProvider>
    </ClerkProvider>
  </React.StrictMode>,
)